import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { useLocalizeMessage } from 'libs.nucleus';
import { MultiStepsModalWindow } from 'libs.react';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { getAssignmentsFromAgentResponse } from './agent_response';
import { applyConsentDocumentsChangeToAssignments, applyEnvironmentSitesChangeToAssignments, } from './apply_local_assignment_changes';
import { BulkAssignmentErrors } from './bulk_assignment_errors.component';
import { BulkAssignmentReviewSkeleton } from './bulk_assignment_review_skeleton.component';
import { BulkAssignmentSummary } from './bulk_assignment_summary.component';
import { SiteConfigurationAssignmentsLayout } from './site_configuration_assignments_layout.component';
import { ViewMode } from './view_selector.component';
import { sendPrompt } from '../agent_studio_api/send_prompt';
import { CONSENT_LIGHT_AGENT_ID } from '../consent_agent/consent_agent.types';
import { ConsentUploadZone } from '../consent_view/components/consent_upload_zone.component';
export const BulkAssignmentModal = ({ onClose, onSubmitAssignments, environmentName, environmentSites, documents, }) => {
    const translate = useLocalizeMessage();
    const agentStudioClient = useApiClient(ApiClientService.AGENT_STUDIO_API);
    const [modalFiles, setModalFiles] = useState([]);
    const [modalAssignments, setModalAssignments] = useState([]);
    const [unrecognizedSites, setUnrecognizedSites] = useState([]);
    const [unrecognizedIcfDocuments, setUnrecognizedIcfDocuments] = useState([]);
    const [isFetchingAssignments, setIsFetchingAssignments] = useState(false);
    const [fetchAssignmentsError, setFetchAssignmentsError] = useState();
    const [reviewViewMode, setReviewViewMode] = useState(ViewMode.SITES_VIEW);
    const [selectedSummaryCard, setSelectedSummaryCard] = useState();
    const handleFileAdd = useCallback((files) => {
        setModalFiles((prev) => [...prev, ...files]);
    }, []);
    const handleClose = useCallback(() => {
        setModalFiles([]);
        setModalAssignments([]);
        setUnrecognizedSites([]);
        setUnrecognizedIcfDocuments([]);
        setIsFetchingAssignments(false);
        setFetchAssignmentsError(undefined);
        setReviewViewMode(ViewMode.SITES_VIEW);
        setSelectedSummaryCard(undefined);
        onClose();
    }, [onClose]);
    const handleSubmit = useCallback(() => {
        onSubmitAssignments(modalAssignments);
        handleClose();
    }, [handleClose, modalAssignments, onSubmitAssignments]);
    const handleModalConsentDocumentsChange = useCallback((environmentSiteId, selectedConsentDocumentIds, consentDocumentIdsInScope) => {
        setModalAssignments((prev) => {
            const next = applyConsentDocumentsChangeToAssignments(prev, environmentSiteId, selectedConsentDocumentIds, consentDocumentIdsInScope);
            return next;
        });
    }, []);
    const handleModalEnvironmentSitesChange = useCallback((documentId, selectedEnvironmentSiteIds, sitesForModal) => {
        setModalAssignments((prev) => {
            const next = applyEnvironmentSitesChangeToAssignments(prev, documentId, selectedEnvironmentSiteIds, sitesForModal);
            return next;
        });
    }, []);
    const handleStepChange = useCallback(async (step) => {
        if (step !== 1) {
            return;
        }
        setIsFetchingAssignments(true);
        setFetchAssignmentsError(undefined);
        setModalAssignments([]);
        setUnrecognizedSites([]);
        setUnrecognizedIcfDocuments([]);
        setSelectedSummaryCard(undefined);
        void sendPrompt(agentStudioClient, CONSENT_LIGHT_AGENT_ID, { files: modalFiles }).then((response) => {
            const { assignments, unrecognizedSites: nextUnrecognizedSites, unrecognizedIcfDocuments: nextUnrecognizedIcfDocuments, } = getAssignmentsFromAgentResponse(response.data, documents, environmentSites);
            setModalAssignments(assignments);
            setUnrecognizedSites(nextUnrecognizedSites);
            setUnrecognizedIcfDocuments(nextUnrecognizedIcfDocuments);
            setIsFetchingAssignments(false);
        }, (error) => {
            console.error(error);
            setFetchAssignmentsError(translate('Something went wrong while generating assignments.'));
            setIsFetchingAssignments(false);
        });
    }, [agentStudioClient, documents, environmentSites, modalFiles, translate]);
    const uploadStep = useMemo(() => (_jsx("div", { className: 'nucleus-flex nucleus-flex-col nucleus-w-full nucleus-gap-4', children: _jsx(ConsentUploadZone, { onFileAdd: handleFileAdd }) }, 'bulk-assignment-step-upload')), [handleFileAdd, modalFiles.length]);
    const reviewStep = useMemo(() => (_jsx("div", { className: 'nucleus-flex nucleus-flex-col nucleus-w-full nucleus-gap-4', children: isFetchingAssignments ? (_jsx(BulkAssignmentReviewSkeleton, {})) : fetchAssignmentsError != null ? (_jsx("div", { className: 'nucleus-flex nucleus-flex-col nucleus-items-center nucleus-justify-center nucleus-py-10', children: fetchAssignmentsError })) : (_jsxs(_Fragment, { children: [_jsx(BulkAssignmentSummary, { assignments: modalAssignments, documents: documents, environmentSites: environmentSites, unrecognizedSitesCount: unrecognizedSites.length, unrecognizedIcfDocumentsCount: unrecognizedIcfDocuments.length, selectedSummaryCard: selectedSummaryCard, onSelectedSummaryCardChange: setSelectedSummaryCard }), selectedSummaryCard === 'error' ? (_jsx(BulkAssignmentErrors, { unrecognizedSites: unrecognizedSites, unrecognizedIcfDocuments: unrecognizedIcfDocuments })) : (_jsx(SiteConfigurationAssignmentsLayout, { viewMode: reviewViewMode, onViewModeChange: setReviewViewMode, environmentSites: environmentSites, consentFiles: documents, assignments: modalAssignments, sitesTableIsLoading: false, sitesTableLabelNoResults: selectedSummaryCard === 'success'
                        ? translate('No sites mapped to ICF documents')
                        : selectedSummaryCard === 'warning'
                            ? translate('No sites remain unmapped')
                            : translate('No sites found for this environment.'), icfDocumentsTableLabelNoResults: selectedSummaryCard === 'success'
                        ? translate('No ICF documents mapped to sites')
                        : selectedSummaryCard === 'warning'
                            ? translate('No ICF documents remain unmapped')
                            : translate('No consent files have been added'), hideUnassigned: selectedSummaryCard === 'success', hideAssigned: selectedSummaryCard === 'warning', onAssignedConsentDocumentsChange: handleModalConsentDocumentsChange, onAssignedEnvironmentSitesChange: handleModalEnvironmentSitesChange }))] })) }, 'bulk-assignment-step-review')), [
        documents,
        environmentSites,
        handleModalConsentDocumentsChange,
        handleModalEnvironmentSitesChange,
        fetchAssignmentsError,
        isFetchingAssignments,
        modalAssignments,
        reviewViewMode,
        selectedSummaryCard,
        translate,
        unrecognizedIcfDocuments,
        unrecognizedSites,
    ]);
    const steps = useMemo(() => [uploadStep, reviewStep], [uploadStep, reviewStep]);
    const hasUnrecognizedSitesOrIcfDocuments = unrecognizedSites.length + unrecognizedIcfDocuments.length > 0;
    const stepStates = useMemo(() => [{ isEnabled: modalFiles.length > 0 }, { isEnabled: !hasUnrecognizedSitesOrIcfDocuments }], [hasUnrecognizedSitesOrIcfDocuments, modalFiles.length]);
    return (_jsx(MultiStepsModalWindow, { title: translate('Upload Spreadsheet'), subtitle: environmentName, steps: steps, stepStates: stepStates, showStepText: true, submitLabel: translate('Done'), onSubmit: handleSubmit, onCancel: handleClose, onStepChange: handleStepChange }));
};
